import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Plus, Pencil, Copy, Trash2, PlayCircle, Share2, LayoutTemplate, User,
} from 'lucide-react';
import { toast } from 'sonner';
import ProjectTemplateEditorDemo, { blankTemplate } from './ProjectTemplateEditorDemo';

export const STORAGE_PREFIX = 'consistency_demo_templates_';
const uid = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

export function loadTemplates(creatorId) {
  try { return JSON.parse(localStorage.getItem(`${STORAGE_PREFIX}${creatorId}`)) || []; }
  catch { return []; }
}
export function saveTemplates(creatorId, templates) {
  localStorage.setItem(`${STORAGE_PREFIX}${creatorId}`, JSON.stringify(templates));
}
function relativeDate(iso) {
  if (!iso) return 'Never';
  const diffMs = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diffMs / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days} days ago`;
  return new Date(iso).toLocaleDateString();
}

export default function ProjectTemplateLibraryDemo({ open, onOpenChange, creatorId, onApplyTemplate }) {
  const [templates, setTemplates] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [shareTarget, setShareTarget] = useState(null);
  const [shareMode, setShareMode] = useState('private');
  const [shareRecipient, setShareRecipient] = useState('');

  useEffect(() => { if (open && creatorId) setTemplates(loadTemplates(creatorId)); }, [open, creatorId]);

  const persist = useCallback((next) => { setTemplates(next); saveTemplates(creatorId, next); }, [creatorId]);

  const handleCreate = () => { setEditingTemplate(null); setEditorOpen(true); };
  const handleEdit = (tpl) => { setEditingTemplate(tpl); setEditorOpen(true); };
  const handleDuplicate = (tpl) => {
    const copy = { ...JSON.parse(JSON.stringify(tpl)), id: uid(), name: `${tpl.name} (Copy)`, updated_at: new Date().toISOString() };
    persist([copy, ...templates]);
    toast.success('Template duplicated');
  };
  const handleSaveFromEditor = (tpl) => {
    const exists = templates.some(t => t.id === tpl.id);
    persist(exists ? templates.map(t => t.id === tpl.id ? tpl : t) : [{ ...tpl, updated_at: new Date().toISOString() }, ...templates]);
    setEditorOpen(false);
    toast.success(exists ? 'Template updated' : 'Template created');
  };
  const confirmDelete = () => {
    persist(templates.filter(t => t.id !== deleteTarget.id));
    toast.success('Template deleted');
    setDeleteTarget(null);
  };
  const handleUse = (tpl) => {
    onApplyTemplate?.(JSON.parse(JSON.stringify(tpl)));
    toast.success(`Applied "${tpl.name}" to this project (demo) — the original template is unchanged.`);
    onOpenChange(false);
  };
  const handleShare = () => {
    const msg = shareMode === 'private'
      ? 'Kept private — nothing was shared.'
      : shareMode === 'team'
        ? 'Demo Mode: team sharing is simulated. No real invitation was sent.'
        : `Demo Mode: sharing with "${shareRecipient || 'that creator'}" is simulated. No real invitation was sent.`;
    toast.info(msg);
    setShareTarget(null);
    setShareMode('private');
    setShareRecipient('');
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-[#14171F] border-white/10 sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="template-library-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2"><LayoutTemplate className="h-5 w-5 text-[#3A7BFF]" />Project Templates</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-[#8A93A6] -mt-3">Reusable production blueprints that belong to you, not any single project — apply the same template across any project or series.</p>

          <div className="flex justify-end">
            <Button size="sm" className="bg-[#3A7BFF] hover:bg-[#3A7BFF]/80" onClick={handleCreate} data-testid="create-template-btn">
              <Plus className="h-4 w-4 mr-2" />Create Template
            </Button>
          </div>

          {templates.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-white/10 rounded-xl" data-testid="template-library-empty">
              <LayoutTemplate className="h-7 w-7 text-[#555E6E] mx-auto mb-2" />
              <p className="text-[#8A93A6] text-sm">No templates yet — create one to reuse across your projects.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="template-library-grid">
              {templates.map(tpl => (
                <Card key={tpl.id} className="glass border-white/5" data-testid={`template-card-${tpl.id}`}>
                  <CardContent className="pt-4 pb-4 space-y-2.5">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-white font-medium truncate">{tpl.name}</p>
                        <p className="text-xs text-[#8A93A6] line-clamp-2">{tpl.description || 'No description'}</p>
                      </div>
                      <Badge variant="outline" className="border-white/10 text-[#8A93A6] text-[10px] shrink-0">{tpl.content_type}</Badge>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {tpl.segments.slice(0, 4).map(s => <Badge key={s.id} className="bg-white/5 text-[#C0C7D4] border-white/10 text-[10px]">{s.name}</Badge>)}
                      {tpl.segments.length > 4 && <Badge className="bg-white/5 text-[#8A93A6] border-white/10 text-[10px]">+{tpl.segments.length - 4} more</Badge>}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {tpl.outputs.map(o => <Badge key={o.id} variant="outline" className="border-[#3A7BFF]/30 text-[#3A7BFF] text-[10px]">{o.label}{o.type === 'shortform' ? ` ×${o.target_clip_count || 1}` : ''}</Badge>)}
                    </div>
                    <div className="flex items-center justify-between text-[11px] text-[#555E6E] pt-1">
                      <span className="flex items-center gap-1"><User className="h-3 w-3" />You</span>
                      <span>Updated {relativeDate(tpl.updated_at)}</span>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap pt-1">
                      <Button size="sm" className="h-7 text-xs bg-[#3A7BFF] hover:bg-[#3A7BFF]/80" onClick={() => handleUse(tpl)} data-testid={`template-use-${tpl.id}`}><PlayCircle className="h-3 w-3 mr-1" />Use</Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs border-white/10" onClick={() => handleEdit(tpl)} data-testid={`template-edit-${tpl.id}`}><Pencil className="h-3 w-3" /></Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs border-white/10" onClick={() => handleDuplicate(tpl)} data-testid={`template-duplicate-${tpl.id}`}><Copy className="h-3 w-3" /></Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs border-white/10" onClick={() => setShareTarget(tpl)} data-testid={`template-share-${tpl.id}`}><Share2 className="h-3 w-3" /></Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs border-white/10 text-red-400 hover:text-red-300" onClick={() => setDeleteTarget(tpl)} data-testid={`template-delete-${tpl.id}`}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ProjectTemplateEditorDemo open={editorOpen} onOpenChange={setEditorOpen} template={editingTemplate} onSave={handleSaveFromEditor} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#14171F] border-white/10" data-testid="template-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#8A93A6]">This only removes the template from your library. Projects that already used it keep their own copy.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/10 bg-transparent text-white" data-testid="template-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={confirmDelete} data-testid="template-delete-confirm-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!shareTarget} onOpenChange={(v) => !v && setShareTarget(null)}>
        <DialogContent className="bg-[#14171F] border-white/10 sm:max-w-md" data-testid="template-share-dialog">
          <DialogHeader><DialogTitle className="text-white">Share "{shareTarget?.name}"</DialogTitle></DialogHeader>
          <RadioGroup value={shareMode} onValueChange={setShareMode} className="space-y-2">
            <label className="flex items-center gap-2 text-sm text-white cursor-pointer"><RadioGroupItem value="private" data-testid="share-mode-private" /><span>Private (only you)</span></label>
            <label className="flex items-center gap-2 text-sm text-white cursor-pointer"><RadioGroupItem value="team" data-testid="share-mode-team" /><span>Share with team</span></label>
            <label className="flex items-center gap-2 text-sm text-white cursor-pointer"><RadioGroupItem value="creator" data-testid="share-mode-creator" /><span>Share with another creator</span></label>
          </RadioGroup>
          {shareMode === 'creator' && (
            <Input value={shareRecipient} onChange={e => setShareRecipient(e.target.value)} placeholder="Creator handle or email" className="bg-white/5 border-white/10 text-white" data-testid="share-recipient-input" />
          )}
          <p className="text-[11px] text-[#555E6E]">Demo Mode: sharing is simulated. No real invitation, permission, or marketplace listing is created.</p>
          <Button className="bg-[#3A7BFF] hover:bg-[#3A7BFF]/80 w-full" onClick={handleShare} data-testid="share-confirm-btn">Share (Demo)</Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
